import React, { useEffect, useMemo, useRef, useState } from "react";
import Modal from "react-modal";
import { registerLocale } from "react-datepicker";
import { createPortal } from "react-dom";
import es from "date-fns/locale/es";
import "react-datepicker/dist/react-datepicker.css";
import { X } from "lucide-react";
import SectionCard from "./SectionCard";
import GeneralSection from "./GeneralSection";
import ClienteSection from "./ClienteSection";
import ArticulosSection from "./ArticulosSection";
import TerminosSection from "./TerminosSection";
import { useDropdownPosition } from "../hooks/useDropdownPosition";
import { useClickOutsideMany } from "../hooks/useClickOutsideMany";

const esDomingo = {
  ...es,
  options: { ...(es?.options || {}), weekStartsOn: 0 },
};
registerLocale("es-dom", esDomingo);

const DatePickerPopper = ({ children }) =>
  createPortal(children, document.body);

export default function ModalCotizacion(props) {
  const {
    isOpen,
    onRequestClose,
    mode = "create",
    onSubmit,
    onCancel,
    markDirty,
    numeroEfectivo,
    setNumeroSobrescrito,
    abrirConfiguracion,
    fechaCotizacion,
    setFechaCotizacion,
    setFechaVencimiento,
    vigenciaDias,
    setVigenciaDias,
    divisa,
    setDivisa,
    ivaPct,
    setIvaPct,
    incoterm,
    setIncoterm,
    vendedores = [],
    vendedorId,
    setVendedorId,
    setVendedorNombre,
    setVendedorCorreo,
    asunto,
    setAsunto,
    clientes = [],
    cliente,
    setCliente,
    selectedCliente,
    atencion,
    setAtencion,
    clienteEmail,
    setClienteEmail,
    clienteTelefono,
    setClienteTelefono,
    datos = [],
    addArticulo,
    eliminar,
    actualizarCantidad,
    handleInputChange,
    setDatos,
    subtotalBase,
    iva,
    total,
    condicionesPago,
    setCondicionesPago,
    tiempoEntrega,
    setTiempoEntrega,
    notas,
    setNotas,
    invalidFields = {},
  } = props;

  const [openGeneral, setOpenGeneral] = useState(true);
  const [openCliente, setOpenCliente] = useState(true);
  const [openArticulos, setOpenArticulos] = useState(true);
  const [openTerminos, setOpenTerminos] = useState(true);
  const [clienteOpen, setClienteOpen] = useState(false);
  const [cpOpen, setCpOpen] = useState(false);
  const [teOpen, setTeOpen] = useState(false);
  const clienteAnchorRef = useRef(null);
  const clientePanelRef = useRef(null);
  const cpAnchorRef = useRef(null);
  const cpPanelRef = useRef(null);
  const teAnchorRef = useRef(null);
  const tePanelRef = useRef(null);

  const { pos: clientePos } = useDropdownPosition({
    open: clienteOpen,
    anchorRef: clienteAnchorRef,
    maxPanelHeight: 380,
  });

  const { pos: cpPos } = useDropdownPosition({
    open: cpOpen,
    anchorRef: cpAnchorRef,
    maxPanelHeight: 350,
  });

  const { pos: tePos } = useDropdownPosition({
    open: teOpen,
    anchorRef: teAnchorRef,
    maxPanelHeight: 350,
  });

  useClickOutsideMany([
    {
      enabled: clienteOpen,
      refs: [clienteAnchorRef, clientePanelRef],
      onOutside: () => setClienteOpen(false),
    },
    {
      enabled: cpOpen,
      refs: [cpAnchorRef, cpPanelRef],
      onOutside: () => setCpOpen(false),
    },
    {
      enabled: teOpen,
      refs: [teAnchorRef, tePanelRef],
      onOutside: () => setTeOpen(false),
    },
  ]);

  useEffect(() => {
    if (!isOpen) return;
    setClienteOpen(false);
    setCpOpen(false);
    setTeOpen(false);
  }, [isOpen]);

  const modalStyle = useMemo(
    () => ({
      overlay: {
        backgroundColor: "rgba(15, 23, 42, 0.45)",
        zIndex: 1400,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 16,
      },
      content: {
        position: "relative",
        inset: "auto",
        width: "min(1120px, 96vw)",
        height: "min(92vh, 860px)",
        border: "none",
        borderRadius: 16,
        padding: 0,
        overflow: "hidden",
        background: "#fff",
        boxShadow: "0 25px 60px rgba(2, 6, 23, 0.25)",
        fontFamily:
          "Roboto, system-ui, -apple-system, Segoe UI, Arial, sans-serif",
      },
    }),
    [],
  );

  return (
    <Modal
      isOpen={Boolean(isOpen)}
      onRequestClose={onRequestClose || onCancel}
      shouldCloseOnOverlayClick
      shouldCloseOnEsc
      style={modalStyle}
      ariaHideApp={false}
    >
      <form className="cf-root" onSubmit={onSubmit} noValidate>
        <div className="cf-header">
          <div className="cf-header-row">
            <div>
              <h2 className="cf-title">
                {mode === "edit"
                  ? "Editar Cotización"
                  : mode === "clone"
                    ? "Nueva Cotización"
                    : "Nueva Cotización"}
              </h2>

              <p className="cf-subtitle">
                {mode === "edit"
                  ? "Actualice los datos necesarios y guarde los cambios"
                  : mode === "clone"
                    ? "Complete los datos para generar la cotización"
                    : "Complete los datos para generar la cotización"}
              </p>
            </div>

            <button
              type="button"
              className="cf-close"
              onClick={onCancel}
              aria-label="Cerrar"
            >
              <X size={20} className="cf-close-icon" />
            </button>
          </div>
        </div>

        <div className="cf-body">
          <SectionCard
            title="Información General"
            open={openGeneral}
            onToggle={() => setOpenGeneral((v) => !v)}
          >
            <GeneralSection
              numeroEfectivo={numeroEfectivo}
              setNumeroSobrescrito={setNumeroSobrescrito}
              abrirConfiguracion={abrirConfiguracion}
              fechaCotizacion={fechaCotizacion}
              setFechaCotizacion={setFechaCotizacion}
              setFechaVencimiento={setFechaVencimiento}
              vigenciaDias={vigenciaDias}
              setVigenciaDias={setVigenciaDias}
              divisa={divisa}
              setDivisa={setDivisa}
              ivaPct={ivaPct}
              setIvaPct={setIvaPct}
              incoterm={incoterm}
              setIncoterm={setIncoterm}
              vendedores={vendedores}
              vendedorId={vendedorId}
              setVendedorId={setVendedorId}
              setVendedorNombre={setVendedorNombre}
              setVendedorCorreo={setVendedorCorreo}
              asunto={asunto}
              setAsunto={setAsunto}
              markDirty={markDirty}
              DatePickerPopper={DatePickerPopper}
              invalidFields={invalidFields}
            />
          </SectionCard>

          <div className="cf-divider" />

          <SectionCard
            title="Información del Cliente"
            open={openCliente}
            onToggle={() => setOpenCliente((v) => !v)}
          >
            <ClienteSection
              clientes={clientes}
              cliente={cliente}
              setCliente={setCliente}
              selectedCliente={selectedCliente}
              atencion={atencion}
              setAtencion={setAtencion}
              clienteEmail={clienteEmail}
              setClienteEmail={setClienteEmail}
              clienteTelefono={clienteTelefono}
              setClienteTelefono={setClienteTelefono}
              markDirty={markDirty}
              clienteOpen={clienteOpen}
              setClienteOpen={setClienteOpen}
              clienteAnchorRef={clienteAnchorRef}
              clientePanelRef={clientePanelRef}
              clientePos={clientePos}
              invalidFields={invalidFields}
            />
          </SectionCard>

          <div className="cf-divider" />

          <SectionCard
            title="Conceptos"
            open={openArticulos}
            onToggle={() => setOpenArticulos((v) => !v)}
          >
            <ArticulosSection
              datos={datos}
              addArticulo={addArticulo}
              eliminar={eliminar}
              actualizarCantidad={actualizarCantidad}
              handleInputChange={handleInputChange}
              setDatos={setDatos}
              markDirty={markDirty}
              subtotalBase={subtotalBase}
              iva={iva}
              total={total}
              ivaPct={ivaPct}
              divisa={divisa}
            />
          </SectionCard>

          <div className="cf-divider" />

          <SectionCard
            title="Términos y Condiciones"
            open={openTerminos}
            onToggle={() => setOpenTerminos((v) => !v)}
          >
            <TerminosSection
              condicionesPago={condicionesPago}
              setCondicionesPago={setCondicionesPago}
              tiempoEntrega={tiempoEntrega}
              setTiempoEntrega={setTiempoEntrega}
              notas={notas}
              setNotas={setNotas}
              markDirty={markDirty}
              cpOpen={cpOpen}
              setCpOpen={setCpOpen}
              teOpen={teOpen}
              setTeOpen={setTeOpen}
              cpAnchorRef={cpAnchorRef}
              cpPanelRef={cpPanelRef}
              cpPos={cpPos}
              teAnchorRef={teAnchorRef}
              tePanelRef={tePanelRef}
              tePos={tePos}
              invalidFields={invalidFields}
            />
          </SectionCard>
        </div>

        <div className="cf-footer">
          <button type="button" className="cf-secondary" onClick={onCancel}>
            Cancelar
          </button>
          <button type="submit" className="cf-primary" formNoValidate>
            Guardar Cotización
          </button>
        </div>
      </form>
    </Modal>
  );
}
